/**
 * Route Overview (order detail) — row-building and label helpers.
 *
 * Leaf module: no app-constant / component imports so it stays loadable in a plain node test.
 * Mirrors WALHQ's `Overview/LegOverview/legOverview.helpers.ts`; the stop-count semantics follow the
 * customer-facing design ("3 Stops" for a two-leg engagement, "Stop 2 of 3" for its nested row).
 */

type TFn = (key: string, options?: Record<string, any>) => string;

/**
 * The engagement a leg belongs to. A run is one carrier hauling several legs: the anchor owns the
 * carrier and each covered leg points at it through carrierSourceLegId. The portal backend may carry
 * the pointer on the leg itself or on its carrierPrice (covered-carrier resolution), so read both.
 */
const getCarrierSourceLegId = (leg: any): number | null => {
    const raw = leg?.carrierSourceLegId ?? leg?.carrierPrice?.carrierSourceLegId ?? null;

    return raw == null || raw === '' ? null : Number(raw);
};

export const isCoveredLeg = (leg: any): boolean => getCarrierSourceLegId(leg) != null;

const sortByPosition = (legs: any[]): any[] =>
    (legs ?? [])
        .filter((leg: any) => !leg?.isDeleted)
        .slice()
        .sort((a: any, b: any) => (a?.legPosition ?? 0) - (b?.legPosition ?? 0));

/**
 * Route rows: every leg, with the legs an ENGAGEMENT covers grouped under the leg that holds it.
 * Covered legs are real legs with real data, so they are ordered beneath their anchor, never replaced
 * by synthetic rows. A covered leg whose anchor is missing is appended rather than silently dropped.
 */
export const buildRouteRows = (legs: any[]): any[] => {
    const live = sortByPosition(legs);
    const anchors = live.filter((leg: any) => !isCoveredLeg(leg));
    const coveredByAnchor = new Map<number, any[]>();

    live.forEach((leg: any) => {
        const key = getCarrierSourceLegId(leg);
        if (key == null) return;
        coveredByAnchor.set(key, [...(coveredByAnchor.get(key) ?? []), leg]);
    });

    const rows = anchors.flatMap((anchor: any) => [anchor, ...(coveredByAnchor.get(Number(anchor.id)) ?? [])]);
    const placed = new Set(rows.map((row: any) => Number(row.id)));

    return [...rows, ...live.filter((leg: any) => !placed.has(Number(leg.id)))];
};

/**
 * Show by Stop OFF: one row per engagement. The anchor is widened to the whole run — final consignee,
 * final delivery schedule, and every dimension that rode any of its legs. engagementLegs keeps the
 * members for anything that still needs per-leg detail.
 */
export const buildCollapsedRouteRows = (legs: any[]): any[] => {
    const live = sortByPosition(legs);
    const anchorIds = new Set(live.filter((leg: any) => !isCoveredLeg(leg)).map((leg: any) => Number(leg.id)));

    return live
        .filter((leg: any) => !isCoveredLeg(leg) || !anchorIds.has(getCarrierSourceLegId(leg) as number))
        .map((anchor: any) => {
            if (isCoveredLeg(anchor)) return anchor;

            const covered = live.filter((leg: any) => getCarrierSourceLegId(leg) === Number(anchor.id));
            if (covered.length === 0) return anchor;

            const members = [anchor, ...covered];
            const last = members[members.length - 1];
            const seenDimIds = new Set<number>();
            const dimensions = members.flatMap((leg: any) =>
                (leg?.order_dimensions ?? []).filter((dim: any) => {
                    if (dim?.id == null) return true;
                    if (seenDimIds.has(Number(dim.id))) return false;
                    seenDimIds.add(Number(dim.id));

                    return true;
                })
            );

            return {
                ...anchor,
                consignee: last?.consignee ?? anchor?.consignee,
                deliveryScheduleSource: last?.pickupDeliverySchedule ?? anchor?.pickupDeliverySchedule,
                order_dimensions: dimensions,
                isEngagementSummary: true,
                engagementLegs: members,
            };
        });
};

/** Live legs of the engagement `leg` belongs to (anchor first), in legPosition order. */
const getEngagementMembers = (leg: any, legs: any[]): any[] => {
    const anchorId = getCarrierSourceLegId(leg) ?? Number(leg?.id);

    return sortByPosition(legs).filter((item: any) => Number(item?.id) === anchorId || getCarrierSourceLegId(item) === anchorId);
};

/**
 * Type column. An engagement of N legs calls at N+1 stops (origin, each hand-off, destination):
 *   • anchor row  → "3 Stops" (or "Direct" when nothing is covered)
 *   • covered row → "Stop k of N+1", the anchor being stop 1 so the first nested row is "Stop 2 of 3"
 * Consolidated spans (Display Order Legs off) have no covered legs and therefore read "Direct".
 */
export const getRouteTypeLabel = (leg: any, legs: any[], t: TFn): string => {
    const members = getEngagementMembers(leg, legs);
    const stopCount = members.length + 1;

    if (isCoveredLeg(leg)) {
        const position = members.findIndex((item: any) => Number(item?.id) === Number(leg?.id));
        const stopNumber = position >= 0 ? position + 1 : 1;

        return t('Orders.Route_Overview_Table.Stop_Of', { number: stopNumber, total: stopCount });
    }

    if (members.length <= 1) return t('Orders.Route_Overview_Table.Direct');

    return `${stopCount} ${t('Orders.Route_Overview_Table.Stops')}`;
};

/** "City, ST" for table cells; the full address is kept for the hover tooltip. */
export const formatLegLocation = (address: any): string => [address?.city, address?.stateCode].filter(Boolean).join(', ') || address?.fullAddress || '';

/** Collapsed anchors carry the run's final delivery schedule; everything else uses its own. */
export const getLegDeliveryScheduleSource = (leg: any): any => leg?.deliveryScheduleSource ?? leg?.pickupDeliverySchedule;

export const resolveLegCarrierName = (leg: any): string => leg?.carrierPrice?.carrier?.name || leg?.carrierPrice?.carrierName || '';

const normalizeLegStatus = (status?: string | null): string =>
    String(status || '')
        .toLowerCase()
        .replace(/_/g, '-');

const DELIVERED_OR_LATER_LEG_STATUSES = ['delivered', 'invoice-created', 'invoice-sent', 'invoice-paid', 'completed', 'dispute', 'claim'];
const IN_TRANSIT_OR_LATER_LEG_STATUSES = ['in-transit', ...DELIVERED_OR_LATER_LEG_STATUSES];

/** Freight has left the pickup — the leg is in transit or anything after it (delivered, invoiced, disputed…). */
export const isLegInTransitOrLater = (status?: string | null): boolean => IN_TRANSIT_OR_LATER_LEG_STATUSES.includes(normalizeLegStatus(status));

/** The leg that arrives at the stop a covered row departs from — the previous member of its run. */
const getStopInboundLeg = (leg: any, legs: any[]): any | null => {
    const members = getEngagementMembers(leg, legs);
    const index = members.findIndex((item: any) => Number(item?.id) === Number(leg?.id));

    return index > 0 ? members[index - 1] : null;
};

export type IStopState = 'upcoming' | 'arrived' | 'departed';

/**
 * A stop row IS the outbound leg. Departed once that leg is in transit or later; Arrived once the
 * inbound leg has delivered at the stop; Upcoming before that.
 */
export const getStopState = (leg: any, legs: any[]): IStopState => {
    if (IN_TRANSIT_OR_LATER_LEG_STATUSES.includes(normalizeLegStatus(leg?.orderStatus))) return 'departed';

    const inbound = getStopInboundLeg(leg, legs);
    if (inbound && DELIVERED_OR_LATER_LEG_STATUSES.includes(normalizeLegStatus(inbound?.orderStatus))) return 'arrived';

    return 'upcoming';
};
